import { Wifi, WifiOff, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface NetworkStatusBannerProps {
  /** Whether the browser reports being online */
  isOnline: boolean;
  /** Whether the user was recently offline */
  wasOffline: boolean;
  /** Callback when user dismisses the recovery banner */
  onDismissRecovery?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Banner component that displays network connectivity status.
 *
 * Shows a red banner when offline with a message about changes syncing later.
 * Shows a green banner when coming back online with a dismiss button.
 *
 * @example
 * ```tsx
 * const { isOnline, wasOffline, clearWasOffline } = useNetworkStatus();
 *
 * <NetworkStatusBanner
 *   isOnline={isOnline}
 *   wasOffline={wasOffline}
 *   onDismissRecovery={clearWasOffline}
 * />
 * ```
 */
export function NetworkStatusBanner({
  isOnline,
  wasOffline,
  onDismissRecovery,
  className,
}: NetworkStatusBannerProps) {
  // Only show when offline OR just came back online
  if (isOnline && !wasOffline) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed top-14 left-0 right-0 z-40 px-4 py-2 text-center text-sm font-medium',
        'transition-all duration-300 ease-in-out',
        'shadow-md',
        !isOnline
          ? 'bg-destructive text-destructive-foreground'
          : 'bg-green-600 text-white',
        className
      )}
      role="alert"
      aria-live="polite"
    >
      <div className="container max-w-full flex items-center justify-center gap-2">
        {!isOnline ? (
          <>
            <WifiOff className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>You are offline. Changes will sync when connection is restored.</span>
          </>
        ) : (
          <>
            <Wifi className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>Back online! Your session has been restored.</span>
            {onDismissRecovery && (
              <button
                onClick={onDismissRecovery}
                className="ml-2 rounded p-0.5 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
                aria-label="Dismiss notification"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
